import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FaPlus, FaSave, FaTimes, FaExclamationCircle, FaFlag } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getTags } from '../../../actions/tagActions';
import PathHeader from './PathHeader';
import CourseList from './CourseList';
import CoursePickerModal from './CoursePickerModal';

const PathForm = ({ path = null, courses = [], onSubmit, mode = 'new' }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { tags } = useSelector(state => state.tag);
  const [formData, setFormData] = useState({
    title: path?.title || '',
    description: path?.description || '',
    icon: path?.icon || 'BookOpen',
    rangeMin: path?.rangeMin || 0,
    rangeMax: path?.rangeMax || 10,
    courses: path?.courses || [],
    tags: path?.tags?.map(tag => tag._id) || []
  });
  const [showPicker, setShowPicker] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const isViewMode = mode === 'view';

  // Setup sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    dispatch(getTags());
  }, [dispatch]);

  useEffect(() => {
    if (path) {
      setFormData({
        title: path.title || '',
        description: path.description || '',
        icon: path.icon || 'BookOpen',
        rangeMin: path.rangeMin || 0,
        rangeMax: path.rangeMax || 10,
        courses: path.courses || [],
        tags: path.tags?.map(tag => tag._id) || []
      });
    }
  }, [path]);

  const validateForm = () => {
    const newErrors = {};

    // Title validation
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    // Range validation
    if (formData.rangeMin < 0) {
      newErrors.rangeMin = 'Minimum score cannot be negative';
    }
    if (formData.rangeMax < 0) {
      newErrors.rangeMax = 'Maximum score cannot be negative';
    }
    if (formData.rangeMin > formData.rangeMax) {
      newErrors.range = 'Minimum score must be less than or equal to maximum score';
    }

    // Courses validation
    if (formData.courses.length === 0) {
      newErrors.courses = 'At least one course is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (updates) => {
    setFormData({ ...formData, ...updates });
    // Mark fields as touched when they're changed
    const touchedFields = {};
    Object.keys(updates).forEach(key => {
      touchedFields[key] = true;
    });
    setTouched({ ...touched, ...touchedFields });
    
    // Validate on change if field has been touched
    if (Object.keys(touched).length > 0) {
      validateForm();
    }
  };

  const handleDragEnd = (event) => {
    if (isViewMode) return;

    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = formData.courses.findIndex((c) => c._id === active.id);
      const newIndex = formData.courses.findIndex((c) => c._id === over.id);

      setFormData({
        ...formData,
        courses: arrayMove(formData.courses, oldIndex, newIndex)
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched({ title: true, rangeMin: true, rangeMax: true, courses: true });
    
    if (validateForm()) {
      onSubmit(formData);
    } else {
      // Scroll to the first error if any
      const firstError = document.querySelector('[data-error="true"]');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleCancel = () => {
    navigate('/settings', { state: { activeTab: 'Paths' } });
  };

  const courseListContent = (
    <div className="min-h-[200px]">
      <CourseList 
        courses={formData.courses}
        onRemove={isViewMode ? null : (id) => {
          const newCourses = formData.courses.filter(c => c._id !== id);
          handleFieldChange({ courses: newCourses });
        }}
        isViewMode={isViewMode}
      />
      {formData.courses.length > 0 && (
        <div className="flex items-center gap-2 justify-center mt-4 text-gray-400">
          <FaFlag className="w-4 h-4" />
          <span className="text-sm font-medium">End of Path</span>
        </div>
      )}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6 relative">
      <PathHeader 
        data={formData}
        onChange={handleFieldChange}
        errors={errors}
        touched={touched}
        readOnly={isViewMode}
        tags={formData.tags}
        availableTags={tags}
      />

      {isViewMode ? (
        courseListContent
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={formData.courses.map(c => c._id)}
            strategy={verticalListSortingStrategy}
          >
            {courseListContent}
          </SortableContext>
        </DndContext>
      )}

      {/* Validation message for courses */}
      {!isViewMode && errors.courses && touched.courses && (
        <div className="flex items-center gap-2 text-red-500 text-sm" data-error="true">
          <FaExclamationCircle />
          <span>{errors.courses}</span>
        </div>
      )}

      {/* Action Buttons Group */}
      <div className="relative flex flex-col items-center gap-4">
        {/* Add Button with Popup - Only show if not in view mode */}
        {!isViewMode && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowAddMenu(!showAddMenu)}
              className="w-12 h-12 bg-[#1086a4] hover:bg-[#054581] text-white rounded-full shadow-lg flex items-center justify-center focus:outline-none transition-colors"
            >
              <FaPlus className="w-5 h-5" />
            </button>

            {/* Popup Menu */}
            {showAddMenu && (
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2">
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden min-w-[120px]">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPicker(true);
                      setShowAddMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <span className="w-4 h-4 rounded-sm bg-[#1086a4]/10 flex items-center justify-center">
                      <FaPlus className="w-3 h-3 text-[#1086a4]" />
                    </span>
                    Course
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Save and Cancel Buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="h-10 px-6 bg-white border-2 border-gray-200 text-gray-600 hover:text-red-500 hover:border-red-500 rounded-full shadow-sm flex items-center gap-2 focus:outline-none transition-colors"
          >
            <FaTimes className="w-4 h-4" />
            <span>Back</span>
          </button>
          {!isViewMode && (
            <button
              type="submit"
              className="h-10 px-6 bg-[#1086a4] hover:bg-[#054581] text-white rounded-full shadow-lg flex items-center gap-2 focus:outline-none transition-colors"
            >
              <FaSave className="w-4 h-4" />
              <span>Save Path</span>
            </button>
          )}
        </div>
      </div>

      {showPicker && (
        <CoursePickerModal
          courses={courses.filter(c => !formData.courses.find(fc => fc._id === c._id))}
          onSelect={(selected) => {
            handleFieldChange({ courses: [...formData.courses, ...selected] });
            setShowPicker(false);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </form>
  );
};

export default PathForm; 